/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import uniq from 'lodash/uniq';
import { Alert, FormValidationAlert } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import { PassTypeProps, ReservationProps } from '../props';
import RenderIf from '../utils/JSXUtils';
import {
    optionSelectMessageFormatter,
    optionSelectInputId,
    flexTextAttributeMessageFormatter,
} from './errorHandling/messageUtils';
import { isPassNumberValid, isPassTypeValid } from '../utils';

export default class PassSelectionError extends React.PureComponent {
    static propTypes = {
        validationErrors: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
        submitAttempted: PropTypes.bool,
        lastSubmitAttempt: PropTypes.number,
        needToKnowChecked: PropTypes.bool,
        errors: PropTypes.shape({
            reservations: PropTypes.arrayOf(
                PropTypes.shape({
                    error_code: PropTypes.string,
                    error_message: PropTypes.string,
                })
            ),
            validation: PropTypes.arrayOf(PropTypes.string),
        }),
        passTypes: PropTypes.arrayOf(PassTypeProps),
        reservations: PropTypes.arrayOf(ReservationProps),
    };

    state = {
        localLastSubmitAttempt: 0,
        errors: {},
    };

    // separate from formatValidationErrors because this checks the errors prop for validation errors.
    hasValidationErrors = () => {
        const { errors, submitAttempted, needToKnowChecked } = this.props;
        const reservationErrors = Array.isArray(errors.reservations)
            ? errors.reservations
            : [];
        const reservationErrorsFiltered = reservationErrors.filter(
            ({ error_code }) => error_code !== undefined
        );
        const validationErrors = Array.isArray(errors.validation)
            ? errors.validation
            : [];
        const validationErrorsFiltered = validationErrors.filter((value) => !value);
        return (
            (submitAttempted && !needToKnowChecked) ||
            reservationErrorsFiltered.length ||
            validationErrorsFiltered.length
        );
    };

    formatError(errorVal, passType) {
        let errorMessage = errorVal;
        if (errorMessage.indexOf('equip_description') !== -1) {
            errorMessage = errorMessage.replace('equip_description', 'Boat Description');
        }
        const split = errorMessage.split(': ');
        let header = '';
        let message = errorMessage;
        if (split.length === 3) {
            header = split[1];
            message = `: ${split[2]}`;
        }
        if (split.length === 2) {
            message = split[1];
        }
        if (message.includes('option-select')) {
            const optSelectMsg = optionSelectMessageFormatter(errorMessage, passType);
            if (optSelectMsg !== null) {
                return optSelectMsg;
            }
        }
        if (message.includes('flex-text-attr') || message.includes('flex text attr')) {
            const flexTextAttributeMsg = flexTextAttributeMessageFormatter(
                errorMessage,
                passType
            );
            if (flexTextAttributeMsg !== null) {
                return flexTextAttributeMsg;
            }
        }
        if (header.includes('ZipCode')) {
            header = 'ZIP Code';
        }

        return `${header}${message}`;
    }

    errorObject(error, displayNumber, passType) {
        return {
            inputId: this.inputId(displayNumber, error, passType),
            errorText: this.formatError(error, passType),
        };
    }

    errorSectionHeader(displayNumber, displayName) {
        return `${displayNumber}. ${displayName.toUpperCase()}`;
    }

    inputId(displayNumber, field, passType) {
        switch (true) {
            case field.includes('First Name'):
                return `holder-first-name-${displayNumber}`;
            case field.includes('Last Name'):
                return `holder-last-name-${displayNumber}`;
            case field.includes('Email Confirmation'):
                return `holder-email-confirmation-${displayNumber}`;
            case field.includes('Pass Type'):
                return `discount-pass-type-${displayNumber}-select`;
            case field.includes('Pass ID'):
                return `discount-pass-number-${displayNumber}`;
            case field.includes('Email'):
                return `holder-email-${displayNumber}`;
            case field.includes('personal-vehicle'):
            case field.includes('License Plate Number'):
                return `vehicle-license-${displayNumber}`;
            case field.includes('motorized-boat'):
            case field.includes('Boat Registration Number'):
                return `watercraft-license-${displayNumber}`;
            case field.includes('Start Date'):
                return `start-date-${displayNumber}`;
            case field.includes('option-select'):
                return optionSelectInputId(displayNumber, field, passType);
            case field.includes('flex-text-attr') || field.includes('flex text attr'):
                return `flex-text-attr-${displayNumber}`;
            case field.includes('Address'):
                return `address-line-1-${displayNumber}`;
            case field.includes('City'):
                return `city-${displayNumber}`;
            case field.includes('State'):
                return `state-${displayNumber}`;
            case field.includes('ZipCode'):
            case field.includes('Zip Code'):
                return `zip-code-${displayNumber}`;
            case field.includes('Country'):
                return `country-${displayNumber}`;
            default:
                return `${displayNumber}`;
        }
    }

    // renders lists of errors associated with each reservation
    renderReservationErrors() {
        const {
            validationErrors,
            submitAttempted,
            lastSubmitAttempt,
            needToKnowChecked,
            reservations,
            passTypes,
        } = this.props;
        if (!reservations || reservations.length === 0) {
            if (Object.keys(this.state.errors).length > 0) {
                this.setState((prev) => {
                    return {
                        ...prev,
                        errors: {},
                    };
                });
            }
            return null;
        }

        const errorsObject = {};
        validationErrors.forEach((errors, index) => {
            if (!errors || errors.length === 0) {
                return;
            }
            let passName = '';
            let passType = null;
            if (passTypes) {
                passType = passTypes.find(
                    ({ id }) => id === reservations?.[index]?.pass_type_id
                );
                if (passType && passType.name) {
                    passName = passType.name;
                }
            }

            const errorsItem = uniq(errors);

            errorsObject[this.errorSectionHeader(index + 1, passName)] = errorsItem.map(
                (error) => this.errorObject(error, index + 1, passType)
            );
        });

        if (submitAttempted && lastSubmitAttempt !== this.state.localLastSubmitAttempt) {
            reservations.forEach((item, index) => {
                const passType = passTypes.find(
                    ({ id }) => id === reservations[index]?.pass_type_id
                );

                const passValidationErrors = [];
                const requiresMailingInfo = Boolean(passType?.features?.['mailing-info']);
                if (!item?.holder_info?.first_name) {
                    passValidationErrors.push(
                        this.errorObject(
                            'First Name: First Name is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }
                if (!item?.holder_info?.last_name) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Last Name: Last Name is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (!item?.holder_info?.email) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Email Required: Email is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (
                    item?.holder_info?.email_confirmation &&
                    item?.holder_info?.email !== item?.holder_info?.email_confirmation
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Email Confirmation: Email and confirmation do not match.',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (!item?.holder_info?.zip_code) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Zip Code: Zip Code is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (requiresMailingInfo && !item?.features?.['mailing-info']?.country) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Country: Country is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (requiresMailingInfo && !item?.features?.['mailing-info']?.address_1) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Address: Address is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (
                    !item?.pass_info?.start_date ||
                    item?.pass_info?.start_date === '' ||
                    !gnoment(item?.pass_info?.start_date).isValid() ||
                    gnoment(item?.pass_info?.start_date).isBefore(
                        gnoment().toDate(),
                        'day'
                    )
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Start Date: Start Date is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                const requiresEquipmentTypeLicense = (k, t) => {
                    if (!passType?.features['equipment-info']) return false;
                    if (!passType?.features['equipment-info']?.[k]) return false;
                    if (!passType?.features['equipment-info']?.[k][t]) return false;
                    return (
                        passType?.features['equipment-info']?.[k][t]?.license_required ===
                        true
                    );
                };

                const requiresWatercraftInfo =
                    passType?.features['equipment-info'] &&
                    passType?.features['equipment-info'].watercraft &&
                    Object.keys(passType?.features['equipment-info'].watercraft).length >
                        0;
                if (
                    requiresWatercraftInfo &&
                    requiresEquipmentTypeLicense(
                        'watercraft',
                        item?.pass_info.features['equipment-info'].watercraft.type
                    ) &&
                    !item?.pass_info?.watercraft_info?.fields?.license
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Boat Registration Number: Boat Registration Number is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                const requiresVehicleInfo =
                    passType?.features['equipment-info'] &&
                    passType?.features['equipment-info'].vehicle &&
                    Object.keys(passType?.features['equipment-info'].vehicle).length > 0;
                if (
                    requiresVehicleInfo &&
                    requiresEquipmentTypeLicense(
                        'vehicle',
                        item?.pass_info.features['equipment-info'].vehicle.type
                    ) &&
                    !item?.pass_info?.vehicle_info?.fields?.license
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'License Plate Number: License Plate Number is required',
                            index + 1,
                            passType?.name
                        )
                    );
                }

                if (
                    !isPassTypeValid(
                        item?.features?.discount?.pass_type,
                        item?.features?.discount?.pass_number
                    )
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Pass Type: Pass Type is required.',
                            index + passValidationErrors.length,
                            passType?.name
                        )
                    );
                }

                if (
                    !isPassNumberValid(
                        item?.features?.discount?.pass_type,
                        item?.features?.discount?.pass_number
                    )
                ) {
                    passValidationErrors.push(
                        this.errorObject(
                            'Pass ID: Pass ID is invalid.',
                            index + passValidationErrors.length,
                            passType?.name
                        )
                    );
                }

                errorsObject[this.errorSectionHeader(index + 1, passType?.name)] =
                    passValidationErrors;
            });
            if (!needToKnowChecked) {
                errorsObject.GENERAL = [
                    {
                        inputId: 'need-to-know-checkbox',
                        errorText:
                            'Consent Checkbox: Please read and agree to the important information below.',
                    },
                ];
            }
            this.setState({
                localLastSubmitAttempt: lastSubmitAttempt,
                errors: errorsObject,
            });
        }

        return this.state.errors;
    }

    render() {
        const { errors } = this.props;

        return (
            <div className="pass-selection-error-wrapper">
                <div className="rec-order-details-error">
                    <FormValidationAlert errors={this.renderReservationErrors()} />
                </div>
                <div aria-live="polite">
                    {RenderIf(!this.hasValidationErrors() && Object.keys(errors).length)(
                        <Alert type="error" heading="Error:" hasAriaLive>
                            <ul>
                                {Object.entries(errors).map(([key, message]) => (
                                    <li key={key}>{message}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                </div>
            </div>
        );
    }
}
